import "react-circular-progressbar/dist/styles.css";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter as Router } from "react-router-dom";
import App from "./App";
import "./index.css";
import store from "./store/store";

if (process.env.NODE_ENV === "development") {
  const resizeObserverError =
    /ResizeObserver loop limit exceeded|ResizeObserver loop completed with undelivered notifications/i;

  const messageLooksLikeResizeObserverNoise = (message: unknown): boolean =>
    resizeObserverError.test(String(message || ""));

  const argsLookLikeResizeObserverNoise = (args: unknown[]): boolean =>
    args.some((arg) => messageLooksLikeResizeObserverNoise(arg));

  const suppressResizeObserverOverlay = (event: Event) => {
    const maybeErrorEvent = event as ErrorEvent;
    const maybePromiseEvent = event as PromiseRejectionEvent;
    const rejectionReason = maybePromiseEvent?.reason;
    const message = String(
      maybeErrorEvent?.message ||
        maybeErrorEvent?.error?.message ||
        maybeErrorEvent?.error ||
        (maybePromiseEvent?.reason &&
          (rejectionReason?.message ||
            rejectionReason?.error?.message ||
            rejectionReason?.error ||
            rejectionReason)) ||
        ""
    );
    if (!resizeObserverError.test(message)) return;
    if ("preventDefault" in event) {
      event.preventDefault();
    }
    if ("stopImmediatePropagation" in event) {
      event.stopImmediatePropagation();
    }
    if ("stopPropagation" in event) {
      event.stopPropagation();
    }
  };

  // Webpack dev overlay often listens in bubble phase; register both capture and bubble.
  window.addEventListener("error", suppressResizeObserverOverlay, true);
  window.addEventListener("error", suppressResizeObserverOverlay, false);
  window.addEventListener("unhandledrejection", suppressResizeObserverOverlay, true);
  window.addEventListener("unhandledrejection", suppressResizeObserverOverlay, false);

  const prevOnError = window.onerror;
  window.onerror = function onerrorResizeObserverFilter(
    message,
    source,
    lineno,
    colno,
    error
  ) {
    if (
      messageLooksLikeResizeObserverNoise(message) ||
      messageLooksLikeResizeObserverNoise(error?.message)
    ) {
      return true;
    }
    if (typeof prevOnError === "function") {
      return prevOnError.call(window, message, source, lineno, colno, error) ?? false;
    }
    return false;
  };

  // CRA's development overlay also hooks into console.error; filter the benign ResizeObserver loop noise.
  const originalConsoleError = console.error;
  console.error = (...args: unknown[]) => {
    if (argsLookLikeResizeObserverNoise(args)) return;
    originalConsoleError(...(args as Parameters<typeof originalConsoleError>));
  };

  const originalConsoleWarn = console.warn;
  console.warn = (...args: unknown[]) => {
    if (argsLookLikeResizeObserverNoise(args)) return;
    originalConsoleWarn(...(args as Parameters<typeof originalConsoleWarn>));
  };
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
root.render(
  <Router>
    <Provider store={store}>
      <App />
    </Provider>
  </Router>
);
